'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    CreateFinancialCategoryData,
    FinancialCategoryItem,
    FinancialCategoryType,
    UpdateFinancialCategoryData,
    createFinancialCategory,
    deleteFinancialCategory,
    getFinancialCategoryChildren,
    getFinancialCategoryNextCode,
    getFinancialCategoryTree,
    searchFinancialCategories,
    updateFinancialCategory,
} from '@/lib/api/financial/financial-category-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
    MdAdd,
    MdCategory,
    MdChevronRight,
    MdClose,
    MdDelete,
    MdEdit,
    MdExpandMore,
    MdSearch,
} from 'react-icons/md'
import { toast } from 'react-toastify'

const TYPE_LABELS: Record<FinancialCategoryType, string> = {
    INCOME: 'Receita',
    EXPENSE: 'Despesa',
    BOTH: 'Ambos',
}

const TYPE_BADGE_CLASS: Record<FinancialCategoryType, string> = {
    INCOME: 'badge-success text-white',
    EXPENSE: 'badge-error text-white',
    BOTH: 'badge-info text-white',
}

interface CategoryForm {
    name: string
    type: FinancialCategoryType
    code: string
    parentId: number | null
    active: boolean
}

const initialForm: CategoryForm = {
    name: '',
    type: 'INCOME',
    code: '',
    parentId: null,
    active: true,
}

function TreeNode({
    category,
    level,
    expandedIds,
    childrenMap,
    loadingIds,
    onToggleExpand,
    onEdit,
    onDelete,
    onAddChild,
    canUpdate,
    canDelete,
    canCreate,
}: {
    category: FinancialCategoryItem
    level: number
    expandedIds: Set<number>
    childrenMap: Map<number, FinancialCategoryItem[]>
    loadingIds: Set<number>
    onToggleExpand: (category: FinancialCategoryItem) => void
    onEdit: (category: FinancialCategoryItem) => void
    onDelete: (id: number) => void
    onAddChild: (parent: FinancialCategoryItem) => void
    canUpdate: boolean
    canDelete: boolean
    canCreate: boolean
}) {
    const childCount = category._count?.children || 0
    const isExpanded = expandedIds.has(category.id)
    const isLoading = loadingIds.has(category.id)
    const children = childrenMap.get(category.id)

    return (
        <div>
            <div
                className={`flex items-center gap-2 py-2 px-3 hover:bg-base-200/50 rounded-lg group transition-colors ${level === 0 ? 'bg-base-200/30' : ''}`}
                style={{ paddingLeft: `${level * 24 + 12}px` }}
            >
                <button
                    className={`btn btn-ghost btn-xs btn-square ${childCount === 0 ? 'invisible' : ''}`}
                    onClick={() => onToggleExpand(category)}
                >
                    {isLoading ? (
                        <span className="loading loading-spinner loading-xs"></span>
                    ) : isExpanded ? (
                        <MdExpandMore size={18} />
                    ) : (
                        <MdChevronRight size={18} />
                    )}
                </button>

                <span className={`flex-1 text-sm ${level === 0 ? 'font-bold' : ''}`}>
                    {category.name}
                </span>

                {category.code && (
                    <span className="font-mono text-xs font-semibold text-secondary badge badge-ghost badge-xs">
                        {category.code}
                    </span>
                )}

                <span className={`badge badge-xs ${TYPE_BADGE_CLASS[category.type]}`}>
                    {TYPE_LABELS[category.type]}
                </span>

                {!category.active && (
                    <span className="badge badge-xs badge-ghost">Inativo</span>
                )}

                {childCount > 0 && (
                    <span className="badge badge-xs badge-ghost">{childCount}</span>
                )}

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canCreate && (
                        <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => onAddChild(category)}
                            title="Adicionar subcategoria"
                        >
                            <MdAdd size={14} />
                        </button>
                    )}
                    {canUpdate && (
                        <button
                            className="btn btn-ghost btn-xs"
                            onClick={() => onEdit(category)}
                            title="Editar"
                        >
                            <MdEdit size={14} />
                        </button>
                    )}
                    {canDelete && childCount === 0 && (
                        <button
                            className="btn btn-ghost btn-xs text-error"
                            onClick={() => onDelete(category.id)}
                            title="Excluir"
                        >
                            <MdDelete size={14} />
                        </button>
                    )}
                </div>
            </div>

            {isExpanded && children && (
                <div>
                    {children.map(child => (
                        <TreeNode
                            key={child.id}
                            category={child}
                            level={level + 1}
                            expandedIds={expandedIds}
                            childrenMap={childrenMap}
                            loadingIds={loadingIds}
                            onToggleExpand={onToggleExpand}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onAddChild={onAddChild}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                            canCreate={canCreate}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

export default function FinancialCategoriesPage() {
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('financial', 'financial_categories')

    const [rootCategories, setRootCategories] = useState<FinancialCategoryItem[]>([])
    const [loading, setLoading] = useState(true)
    const [typeFilter, setTypeFilter] = useState<FinancialCategoryType | ''>('')

    // Lazy loading
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set())
    const [childrenMap, setChildrenMap] = useState<Map<number, FinancialCategoryItem[]>>(new Map())
    const [loadingIds, setLoadingIds] = useState<Set<number>>(new Set())

    // Busca
    const [searchInput, setSearchInput] = useState('')
    const [searchResults, setSearchResults] = useState<FinancialCategoryItem[] | null>(null)
    const [searching, setSearching] = useState(false)
    const searchTimeout = useRef<NodeJS.Timeout>(null)

    // Modal
    const modalRef = useRef<HTMLDialogElement>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [form, setForm] = useState<CategoryForm>(initialForm)
    const [loadingCode, setLoadingCode] = useState(false)
    const [saving, setSaving] = useState(false)

    // Delete
    const [deleteId, setDeleteId] = useState<number | null>(null)
    const [deleting, setDeleting] = useState(false)

    // Parent select
    const [allLoadedCategories, setAllLoadedCategories] = useState<FinancialCategoryItem[]>([])

    useEffect(() => {
        loadRoots()
    }, [typeFilter])

    useEffect(() => {
        const flat: FinancialCategoryItem[] = [...rootCategories]
        for (const [, children] of childrenMap) {
            flat.push(...children)
        }
        setAllLoadedCategories(flat)
    }, [rootCategories, childrenMap])

    const loadRoots = async () => {
        setLoading(true)
        setExpandedIds(new Set())
        setChildrenMap(new Map())
        try {
            const params = typeFilter ? { type: typeFilter as FinancialCategoryType } : undefined
            const res = await getFinancialCategoryTree(params)
            if (res.success && res.result) {
                setRootCategories(res.result)
            }
        } catch {
            toast.error('Erro ao carregar categorias financeiras')
        } finally {
            setLoading(false)
        }
    }

    const handleToggleExpand = useCallback(async (category: FinancialCategoryItem) => {
        const id = category.id

        if (expandedIds.has(id)) {
            setExpandedIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
            return
        }

        if (childrenMap.has(id)) {
            setExpandedIds(prev => new Set(prev).add(id))
            return
        }

        setLoadingIds(prev => new Set(prev).add(id))
        try {
            const res = await getFinancialCategoryChildren(id)
            if (res.success && res.result) {
                setChildrenMap(prev => {
                    const next = new Map(prev)
                    next.set(id, res.result!)
                    return next
                })
                setExpandedIds(prev => new Set(prev).add(id))
            }
        } catch {
            toast.error('Erro ao carregar subcategorias')
        } finally {
            setLoadingIds(prev => {
                const next = new Set(prev)
                next.delete(id)
                return next
            })
        }
    }, [expandedIds, childrenMap])

    // Busca com debounce
    useEffect(() => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current)

        if (!searchInput.trim()) {
            setSearchResults(null)
            return
        }

        searchTimeout.current = setTimeout(async () => {
            setSearching(true)
            try {
                const res = await searchFinancialCategories(
                    searchInput.trim(),
                    typeFilter || undefined,
                )
                if (res.success && res.result) {
                    setSearchResults(res.result)
                }
            } catch { /* ignore */ }
            finally { setSearching(false) }
        }, 300)

        return () => {
            if (searchTimeout.current) clearTimeout(searchTimeout.current)
        }
    }, [searchInput, typeFilter])

    const fetchNextCode = async (parentId?: number | null) => {
        setLoadingCode(true)
        try {
            const res = await getFinancialCategoryNextCode(parentId ?? undefined)
            if (res.success && res.result) {
                setForm(prev => ({ ...prev, code: res.result!.code }))
            }
        } catch { /* ignore */ }
        finally { setLoadingCode(false) }
    }

    const openCreateModal = () => {
        setEditingId(null)
        setForm(initialForm)
        modalRef.current?.showModal()
        fetchNextCode(null)
    }

    const openCreateChildModal = (parent: FinancialCategoryItem) => {
        setEditingId(null)
        setForm({ ...initialForm, type: parent.type, parentId: parent.id, code: '' })
        modalRef.current?.showModal()
        fetchNextCode(parent.id)
    }

    const openEditModal = (category: FinancialCategoryItem) => {
        setEditingId(category.id)
        setForm({
            name: category.name,
            type: category.type,
            code: category.code || '',
            parentId: category.parentId,
            active: category.active,
        })
        modalRef.current?.showModal()
    }

    const closeModal = () => {
        modalRef.current?.close()
        setEditingId(null)
        setForm(initialForm)
    }

    const handleParentChange = (parentId: number | null) => {
        setForm(prev => ({ ...prev, parentId }))
        if (!editingId) fetchNextCode(parentId)
    }

    const handleSave = async () => {
        if (!form.name.trim()) {
            toast.warning('Informe o nome da categoria')
            return
        }

        setSaving(true)
        try {
            if (editingId) {
                const data: UpdateFinancialCategoryData = {
                    name: form.name.trim(),
                    type: form.type,
                    code: form.code || undefined,
                    parentId: form.parentId,
                    active: form.active,
                }
                const res = await updateFinancialCategory(editingId, data)
                if (res.success) {
                    toast.success('Categoria atualizada com sucesso')
                    closeModal()
                    loadRoots()
                } else {
                    toast.error(res.error || 'Erro ao atualizar categoria')
                }
            } else {
                const data: CreateFinancialCategoryData = {
                    name: form.name.trim(),
                    type: form.type,
                    code: form.code || undefined,
                    parentId: form.parentId,
                }
                const res = await createFinancialCategory(data)
                if (res.success) {
                    toast.success('Categoria criada com sucesso')
                    closeModal()
                    loadRoots()
                } else {
                    toast.error(res.error || 'Erro ao criar categoria')
                }
            }
        } catch {
            toast.error('Erro ao salvar categoria')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteId) return
        setDeleting(true)
        try {
            const res = await deleteFinancialCategory(deleteId)
            if (res.success) {
                toast.success('Categoria excluída com sucesso')
                setDeleteId(null)
                loadRoots()
            } else {
                toast.error(res.error || 'Erro ao excluir categoria')
            }
        } catch {
            toast.error('Erro ao excluir categoria')
        } finally {
            setDeleting(false)
        }
    }

    const totalCategories = rootCategories.length

    return (
        <div className="p-4 md:p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <MdCategory size={28} className="text-secondary" />
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="text-2xl font-bold">Categorias Financeiras</h1>
                            {totalCategories > 0 && (
                                <span className="badge badge-secondary badge-sm">
                                    {totalCategories}
                                </span>
                            )}
                        </div>
                        <p className="text-base-content/60 text-sm mt-1">
                            Gerencie as categorias financeiras em estrutura hierárquica
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <button className="btn btn-secondary" onClick={openCreateModal}>
                        <MdAdd size={20} />
                        Nova Categoria
                    </button>
                )}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-sm">
                    <MdSearch
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40 z-10"
                    />
                    <input
                        type="text"
                        className="input input-bordered input-sm w-full pl-9"
                        placeholder="Buscar por nome ou código..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                    {searching && (
                        <span className="loading loading-spinner loading-xs absolute right-3 top-1/2 -translate-y-1/2"></span>
                    )}
                </div>

                <select
                    className="select select-bordered select-sm"
                    value={typeFilter}
                    onChange={e => setTypeFilter(e.target.value as FinancialCategoryType | '')}
                >
                    <option value="">Todos os tipos</option>
                    <option value="INCOME">Receita</option>
                    <option value="EXPENSE">Despesa</option>
                    <option value="BOTH">Ambos</option>
                </select>

                {totalCategories > 0 && (
                    <span className="text-sm text-base-content/50 ml-auto">
                        {totalCategories} categoria{totalCategories !== 1 ? 's' : ''} raiz
                    </span>
                )}
            </div>

            {/* Loading */}
            {loading && (
                <div className="flex items-center justify-center min-h-[300px]">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                </div>
            )}

            {/* Empty */}
            {!loading && rootCategories.length === 0 && !searchInput && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body items-center text-center py-16">
                        <MdCategory size={48} className="text-base-content/30" />
                        <p className="text-base-content/60 mt-2">Nenhuma categoria cadastrada</p>
                        {canCreate && (
                            <button className="btn btn-secondary btn-sm mt-4" onClick={openCreateModal}>
                                <MdAdd size={18} />
                                Cadastrar Categoria
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Search results */}
            {searchResults && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body p-4">
                        <p className="text-sm text-base-content/60 mb-3">
                            {searchResults.length} resultado{searchResults.length !== 1 ? 's' : ''} para "{searchInput}"
                        </p>
                        {searchResults.length === 0 && (
                            <p className="text-center text-base-content/40 py-8">Nenhuma categoria encontrada</p>
                        )}
                        {searchResults.map(category => (
                            <div
                                key={category.id}
                                className="flex items-center gap-3 py-2 px-3 hover:bg-base-200/50 rounded-lg group"
                            >
                                <span className="flex-1 text-sm">{category.name}</span>
                                {category.code && (
                                    <span className="font-mono text-xs font-semibold text-secondary badge badge-ghost badge-xs">
                                        {category.code}
                                    </span>
                                )}
                                {category.parent && (
                                    <span className="text-xs text-base-content/40">
                                        Pai: {category.parent.name}
                                    </span>
                                )}
                                <span className={`badge badge-xs ${TYPE_BADGE_CLASS[category.type]}`}>
                                    {TYPE_LABELS[category.type]}
                                </span>
                                {!category.active && (
                                    <span className="badge badge-xs badge-ghost">Inativo</span>
                                )}
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    {canUpdate && (
                                        <button className="btn btn-ghost btn-xs" onClick={() => openEditModal(category)}>
                                            <MdEdit size={14} />
                                        </button>
                                    )}
                                    {canDelete && (
                                        <button className="btn btn-ghost btn-xs text-error" onClick={() => setDeleteId(category.id)}>
                                            <MdDelete size={14} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Tree view */}
            {!loading && rootCategories.length > 0 && !searchResults && (
                <div className="card bg-base-100 shadow">
                    <div className="card-body p-4">
                        {rootCategories.map(category => (
                            <TreeNode
                                key={category.id}
                                category={category}
                                level={0}
                                expandedIds={expandedIds}
                                childrenMap={childrenMap}
                                loadingIds={loadingIds}
                                onToggleExpand={handleToggleExpand}
                                onEdit={openEditModal}
                                onDelete={setDeleteId}
                                onAddChild={openCreateChildModal}
                                canUpdate={canUpdate}
                                canDelete={canDelete}
                                canCreate={canCreate}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Create/Edit Modal */}
            <dialog ref={modalRef} className="modal">
                <div className="modal-box !max-w-lg">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-lg">
                            {editingId ? 'Editar Categoria' : 'Nova Categoria'}
                        </h3>
                        <button className="btn btn-ghost btn-sm btn-square" onClick={closeModal}>
                            <MdClose size={18} />
                        </button>
                    </div>

                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Nome *</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Ex: Consultas Médicas"
                                value={form.name}
                                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Tipo</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={form.type}
                                    onChange={e =>
                                        setForm(prev => ({
                                            ...prev,
                                            type: e.target.value as FinancialCategoryType,
                                        }))
                                    }
                                >
                                    <option value="INCOME">Receita</option>
                                    <option value="EXPENSE">Despesa</option>
                                    <option value="BOTH">Ambos</option>
                                </select>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Código</span>
                                </label>
                                {loadingCode ? (
                                    <div className="input input-bordered w-full flex items-center bg-base-200/50">
                                        <span className="loading loading-spinner loading-xs"></span>
                                    </div>
                                ) : (
                                    <input
                                        type="text"
                                        className="input input-bordered w-full font-mono"
                                        placeholder="Automático"
                                        value={form.code}
                                        onChange={e => setForm(prev => ({ ...prev, code: e.target.value }))}
                                    />
                                )}
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Categoria Pai</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={form.parentId || ''}
                                onChange={e =>
                                    handleParentChange(
                                        e.target.value ? parseInt(e.target.value, 10) : null,
                                    )
                                }
                            >
                                <option value="">Nenhuma (categoria raiz)</option>
                                {allLoadedCategories
                                    .filter(c => c.active && c.id !== editingId)
                                    .map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.code ? `${c.code} - ` : ''}{c.name}
                                        </option>
                                    ))}
                            </select>
                        </div>

                        {editingId && (
                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="toggle toggle-secondary toggle-sm"
                                        checked={form.active}
                                        onChange={e =>
                                            setForm(prev => ({ ...prev, active: e.target.checked }))
                                        }
                                    />
                                    <span className="label-text">Ativa</span>
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="modal-action">
                        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
                            Cancelar
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving && (
                                <span className="loading loading-spinner loading-sm"></span>
                            )}
                            {editingId ? 'Salvar' : 'Criar'}
                        </button>
                    </div>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={closeModal}>close</button>
                </form>
            </dialog>

            {/* Delete confirmation */}
            <ConfirmModal
                isOpen={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Excluir Categoria"
                message="Tem certeza de que deseja excluir esta categoria? Esta ação não pode ser desfeita."
                loading={deleting}
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
